import { PropType } from 'vue';
import { Options, Vue } from 'vue-class-component';
import {
  BuildingCreateDTO,
  BuildingDTO,
  EntrancePlantDetailDTO,
  EntrancePlantStructureCreateDTO,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import plantsClient from '@/core/services/plants';

@Options({
  name: 'DialogAddEditBuilding',
  props: {
    currentPlant: {
      type: Object as PropType<EntrancePlantDetailDTO>,
      required: false,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    buildingToEdit: {
      type: Object as PropType<BuildingDTO>,
      required: false,
    },
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.invalidNameReason = '';
        this.isValidName = true;
        this.initBuilding();
      }
    },
    'building.name': function () {
      if (this.building) {
        this.verifyValidityOfName();
      }
    },
  },

  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddEditBuilding extends Vue {
  currentPlant!: EntrancePlantDetailDTO;
  isVisible!: boolean;
  buildingToEdit!: BuildingDTO;
  isDialogVisible = false;

  isEdit = false;
  building!: BuildingCreateDTO;

  // Naming validation props
  isValidName = true;
  invalidNameReason: string = '';

  mounted() {
    this.isDialogVisible = this.isVisible;
  }

  async initBuilding() {
    this.building = new BuildingCreateDTO();
    if (this.buildingToEdit) {
      this.isEdit = true;
      this.building.buildingId = this.buildingToEdit.buildingId;
      this.building.apartmentsCount = this.buildingToEdit.apartments?.length;
      this.building.name = this.buildingToEdit.name;
    } else {
      this.isEdit = false;
      let i = 0;
      do {
        i++;
        const newName = `${this.$t('AppResourceEP.Building')} ${
          this.currentPlant.buildings!.length + i
        }`;
        this.building.name = newName;
        this.verifyValidityOfName();
      } while (!this.isValidName);
      this.building.apartmentsCount = 1;
    }
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    return this.$t('AppResourceEP.AddNewBuilding');
  }

  get inputLabel() {
    return `${this.$t('AppResource.NameUpper')} ${this.$t(
      'AppResourceEP.Building'
    )}`;
  }

  get buttonSubmitLabel() {
    return this.$t('AppResource.Save');
  }

  get saveButtonDisabled() {
    const disabledForNameCheck =
      !this.isValidName || !this.building?.name?.trim();
    return disabledForNameCheck;
  }

  verifyValidityOfName() {
    if (this.building?.name === undefined) return;

    const nameToCheck = this.building.name.trim();
    let listToCheck = this.currentPlant.buildings || [];

    if (this.isEdit) {
      listToCheck = listToCheck.filter(
        (editing) => editing.buildingId !== this.buildingToEdit.buildingId
      );
    }

    const exist = listToCheck.some(
      (existing) =>
        existing.name?.toLowerCase().trim() == nameToCheck.toLowerCase().trim()
    );
    this.isValidName = !exist;
    this.invalidNameReason = this.isValidName
      ? ''
      : this.$t('general.alreadyExisting');
  }

  async handleButtonSubmitAction() {
    this.isDialogVisible = false;

    await this.saveBuilding();

    this.$emit('update-list');
    this.$toast.add({
      severity: 'success',
      summary: this.$t('entrancePlant.buildingBannerCreated'),
      closable: false,
      life: 3000,
    });
    const summary = this.$t('general.synced');
    setTimeout(() => {
      this.$toast.add({
        severity: 'success',
        summary: summary,
        closable: false,
        life: 3000,
      });
    }, 1500);
  }

  retrieveEditedStructure() {
    const structure = new EntrancePlantStructureCreateDTO();
    structure.buildingsCreate = [];
    this.currentPlant.buildings?.forEach((epBuilding) =>
      structure.buildingsCreate?.push(
        new BuildingCreateDTO({
          buildingId: epBuilding.buildingId,
          name: epBuilding.name,
          apartmentsCount: epBuilding.apartments?.length,
        })
      )
    );

    if (this.isEdit) {
      const editingIndex = structure.buildingsCreate.findIndex(
        (editing) => editing.buildingId === this.building.buildingId
      );
      structure.buildingsCreate.splice(editingIndex, 1, this.building);
    } else {
      structure.buildingsCreate.push(this.building);
    }
    return structure;
  }

  async saveBuilding() {
    if (this.currentPlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      const editStructure = this.retrieveEditedStructure();

      await plantsClient.structure(this.currentPlant.desktopId, editStructure);
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }
}
